import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authFetch, isInteractiveAuthError } from '../authFetch';

// Always hit the deployed API; allow overriding via .env if needed
const GENERATE_UPLOAD_URL_API_ENDPOINT = process.env.REACT_APP_GENERATE_UPLOAD_URL_API_ENDPOINT ||
  'https://kjzma4p2jc.execute-api.eu-west-2.amazonaws.com/prod/generate-upload-url';
const LOG_FLIGHT_DETAILS_API_ENDPOINT = process.env.REACT_APP_WRITE_FLIGHT_LOG_DETAILS_API_ENDPOINT ||
  'https://ovl52u3rf2.execute-api.eu-west-2.amazonaws.com/writeFlightLogDetailsComprehensive-dev';

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalContentStyle = {
  backgroundColor: '#fff',
  padding: '25px 30px',
  border: '1px solid #222',
  borderRadius: '0px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  width: '420px',
  textAlign: 'left',
  fontSize: '0.8rem',
};

const inputStyle = {
  width: 'calc(100% - 22px)',
  padding: '8px 10px',
  marginBottom: '12px',
  border: '1px solid #000',
  borderRadius: '0px',
  fontSize: '0.85rem',
};

const buttonStyle = {
  padding: '8px 18px',
  fontSize: '0.9rem',
  cursor: 'pointer',
  border: 'none',
  borderRadius: '0px',
  marginRight: '10px',
};

function UploadTelemetryModal({ isOpen, onClose, context }) {
  const { accounts } = useMsal();
  const account = accounts[0];

  const [flightName, setFlightName] = useState('');
  const [flightLocation, setFlightLocation] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileName, setCurrentFileName] = useState('');

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files || []));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Please choose at least one telemetry file to upload.');
      return;
    }

    if (isUploading) {
      return; // Prevent multiple uploads
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        setCurrentFileName(file.name);
        
        // Update progress for current file start
        const baseProgress = (i / selectedFiles.length) * 100;
        setUploadProgress(baseProgress);

        // Preserve original file extension (.ulg or .tlog)
        const fileExt = file.name.split('.').pop().toLowerCase();
        if (!['ulg', 'tlog'].includes(fileExt)) {
          alert(`Unsupported file type: ${file.name}. Please upload .ulg or .tlog files only.`);
          continue;
        }

        // Generate a fresh UUID so the object key is unique and detached from local filename
        const newUuid = crypto.randomUUID();
        const s3FileName = `${newUuid}.${fileExt}`;

        // Step 1: Get presigned URL (10% of progress for this file)
        setUploadProgress(baseProgress + 10);
        const presigned = await authFetch(GENERATE_UPLOAD_URL_API_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileName: s3FileName, contentType: file.type || 'application/octet-stream' }),
        });
        
        if (!presigned.ok) throw new Error('Failed to get upload URL');
        const { uploadUrl, key } = await presigned.json();

        // Step 2: Upload to S3 (70% of progress for this file)
        setUploadProgress(baseProgress + 20);
        const uploadResp = await fetch(uploadUrl, { 
          method: 'PUT', 
          body: file, 
          headers: { 'Content-Type': file.type || 'application/octet-stream' } 
        });
        
        if (!uploadResp.ok) throw new Error('S3 upload failed');
        setUploadProgress(baseProgress + 80);

        // Step 3: Log flight details (20% of progress for this file)
        const flightUuid = newUuid;
        const payloadToSend = {
          flight_name: flightName || s3FileName,
          flight_location: flightLocation,
          s3_object_key: key,
          flight_uuid: flightUuid,
          template_id: context?.templateId || null,
          uploader_name: account?.name || 'Unknown',
          uploader_email: account?.username || 'Unknown',
          uploader_oid: account?.idTokenClaims?.oid || 'Unknown',
        };
        
        console.log('[UploadTelemetryModal][Flight Details Payload]', {
          contextReceived: context,
          templateIdExtracted: context?.templateId,
          payloadToSend: payloadToSend
        });
        
        await authFetch(LOG_FLIGHT_DETAILS_API_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payloadToSend),
        });

        // Complete progress for this file
        setUploadProgress((i + 1) / selectedFiles.length * 100);
        
        // Short delay to show completion
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      alert(`Successfully uploaded ${selectedFiles.length} file(s)!`);
      onClose();
    } catch (error) {
      if (isInteractiveAuthError(error)) return;
      console.error(error);
      alert(`Upload failed for ${currentFileName}: ${error.message}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setCurrentFileName('');
    }
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <h2 style={{ textAlign: 'center', marginBottom: 20, fontSize: '1rem' }}>Upload Telemetry File</h2>
        <label htmlFor="flightName">Name:</label>
        <input id="flightName" type="text" value={flightName} onChange={e => setFlightName(e.target.value)} style={inputStyle} />

        <label htmlFor="flightLocation">Location:</label>
        <input id="flightLocation" type="text" value={flightLocation} onChange={e => setFlightLocation(e.target.value)} style={inputStyle} />

        <label htmlFor="telemetryFile" style={{ display: 'block', marginBottom: '4px' }}>Telemetry File (.ulg or .tlog):</label>
        <input id="telemetryFile" type="file" accept=".ulg,.tlog" multiple onChange={handleFileChange} style={{ display: 'none' }} />
        <label htmlFor="telemetryFile" style={{ ...buttonStyle, backgroundColor: '#000', color: '#fff', display: 'inline-block', marginBottom: '12px', cursor:'pointer' }}>
          Choose File(s)
        </label>
        {selectedFiles.length > 0 && (
          <div style={{ fontSize: '0.75rem', marginBottom: '12px' }}>{selectedFiles.length} file(s) selected</div>
        )}

        {isUploading && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '0.75rem', marginBottom: '4px' }}>
              Uploading {currentFileName}... ({Math.round(uploadProgress)}%)
            </div>
            <div style={{ 
              width: '100%', 
              backgroundColor: '#f0f0f0', 
              borderRadius: '4px', 
              overflow: 'hidden',
              height: '20px',
              border: '1px solid #ccc'
            }}>
              <div style={{ 
                width: `${uploadProgress}%`, 
                backgroundColor: '#000', 
                height: '100%',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>
        )}

        <div style={{ marginTop: 25, textAlign: 'right' }}>
          <button 
            onClick={onClose} 
            disabled={isUploading}
            style={{ 
              ...buttonStyle, 
              backgroundColor: 'transparent', 
              color: isUploading ? '#999' : '#000', 
              border: '1px solid #000',
              cursor: isUploading ? 'not-allowed' : 'pointer'
            }}
          >
            Cancel
          </button>
          <button 
            onClick={handleUpload} 
            disabled={isUploading}
            style={{ 
              ...buttonStyle, 
              backgroundColor: isUploading ? '#666' : '#000', 
              color: '#fff',
              cursor: isUploading ? 'not-allowed' : 'pointer'
            }}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UploadTelemetryModal; 